import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/client'
import { endpoints } from '@/shared/api/endpoints'
import { useAppStore } from '@/shared/stores/appStore'
import { leagueKeys } from '@/features/league/queries'
import { squadKeys, playerKeys } from '@/features/squad/queries'

export type PersonsFilter = {
  teamId?:   string
  position?: PersonsPositionFilter
}

export type PersonsPositionFilter = 'GK' | 'DEF' | 'MID' | 'FWD' | 'NONE'

export const adminKeys = {
  all:       ['admin'] as const,
  me:        () => [...adminKeys.all, 'me'] as const,
  gameweeks: (competitionId: string) =>
    [...adminKeys.all, 'gameweeks', competitionId] as const,
  schedules: (gameweekId: string) =>
    [...adminKeys.all, 'schedules', gameweekId] as const,
  personsAll: () => [...adminKeys.all, 'persons'] as const,
  persons:    (competitionId: string, filter: PersonsFilter) =>
    [...adminKeys.all, 'persons', competitionId, filter] as const,
}

// ── Admin identity ────────────────────────────────────────────────────────────

type AdminMeResult = { isAdmin: boolean }

export async function fetchAdminMe(): Promise<AdminMeResult> {
  return apiClient.get<AdminMeResult>(endpoints.admin.me())
}

export function useIsAdmin() {
  return useQuery({
    queryKey: adminKeys.me(),
    queryFn:  fetchAdminMe,
    staleTime: 5 * 60_000,
  })
}

// ── Gameweek list ─────────────────────────────────────────────────────────────

export type GameweekStatus = 'UPCOMING' | 'ACTIVE' | 'LOCKED' | 'FINALIZED'

export type AdminGameweek = {
  id:                     string
  number:                 number
  phase:                  string
  status:                 GameweekStatus
  firstMatchAt:           string
  lastMatchEndsAt:        string
  canHaveExtraTime:       boolean
  scoresFinalizedAt:      string | null
  transferWindowOpensAt:  string | null
  scoresComputed:         boolean   // Process has written scores → Finalize is available
}

export async function fetchAdminGameweeks(competitionId: string): Promise<AdminGameweek[]> {
  return apiClient.get<AdminGameweek[]>(endpoints.gameweeks.list(competitionId))
}

export function useAdminGameweeks(competitionId: string | null) {
  return useQuery({
    queryKey:  adminKeys.gameweeks(competitionId ?? ''),
    queryFn:   () => fetchAdminGameweeks(competitionId as string),
    staleTime: 30_000,
    enabled:   !!competitionId,
  })
}

// ── Gameweek action mutations ─────────────────────────────────────────────────
//
// Every gameweek action invalidates a wide blast radius:
//   - adminKeys.all          → refresh GW list status badges + scores panels
//   - leagueKeys.all         → window state, standings, fixtures all depend on GW state
//   - squadKeys.all          → my-squad reads frozen lock state
//   - playerKeys.all         → per-player gw points
// Cheaper than chasing individual keys per mutation, and mistakes here surface
// as stale state during a live match — the worst time to debug.

function useInvalidateGameweekDependents() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: adminKeys.all })
    void qc.invalidateQueries({ queryKey: leagueKeys.all })
    void qc.invalidateQueries({ queryKey: squadKeys.all })
    void qc.invalidateQueries({ queryKey: playerKeys.all })
  }
}

export type LockGameweekResult = void
export type FetchStatsResult = {
  gameweekId:         string
  matchesProcessed:   number
  statsUpserted:      number
  playersNotFound:    number
  allMatchesFinished: boolean
  warnings:           string[]
}
export type ProcessGameweekResult = { gameweekId: string; processedAt: string }
export type FinalizeGameweekResult = { gameweekId: string; finalizedAt: string }
export type ResetGameweekResult = {
  gameweekId:           string
  wasFinalized:         boolean
  squadScoresDeleted:   number
  playerScoresDeleted:  number
  h2hFixturesReset:     number
  h2hLeaguesRecomputed: number
}
export type ReopenWindowResult = {
  gameweekId: string
  reopenedAt: string
}

export type AdminAction =
  | { kind: 'lock';     result: LockGameweekResult }
  | { kind: 'fetch';    result: FetchStatsResult }
  | { kind: 'process';  result: ProcessGameweekResult }
  | { kind: 'finalize'; result: FinalizeGameweekResult }
  | { kind: 'reset';    result: ResetGameweekResult }
  | { kind: 'reopen';   result: ReopenWindowResult }

export function useLockGameweek() {
  const invalidate = useInvalidateGameweekDependents()
  const addToast   = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (gameweekId: string) =>
      apiClient.post<LockGameweekResult>(endpoints.admin.lockGameweek(gameweekId), {}),
    onSuccess: () => {
      invalidate()
      addToast({ type: 'success', title: 'Rodada bloqueada' })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao bloquear', body: err.message })
    },
  })
}

export function useFetchGameweekStats() {
  const invalidate = useInvalidateGameweekDependents()
  const addToast   = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (gameweekId: string) =>
      apiClient.post<FetchStatsResult>(endpoints.admin.fetchGameweekStats(gameweekId), {}),
    onSuccess: (result) => {
      invalidate()
      addToast({
        type: 'success',
        title: 'Stats carregados',
        body:  `${result.statsUpserted} stats em ${result.matchesProcessed} partidas`,
      })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao buscar stats', body: err.message })
    },
  })
}

export function useProcessGameweek() {
  const invalidate = useInvalidateGameweekDependents()
  const addToast   = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (gameweekId: string) =>
      apiClient.post<ProcessGameweekResult>(endpoints.admin.processGameweek(gameweekId), {}),
    onSuccess: () => {
      invalidate()
      addToast({ type: 'success', title: 'Rodada processada' })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao processar', body: err.message })
    },
  })
}

export function useFinalizeGameweek() {
  const invalidate = useInvalidateGameweekDependents()
  const addToast   = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (gameweekId: string) =>
      apiClient.post<FinalizeGameweekResult>(endpoints.admin.finalizeGameweek(gameweekId), {}),
    onSuccess: () => {
      invalidate()
      addToast({ type: 'success', title: 'Rodada finalizada' })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao finalizar', body: err.message })
    },
  })
}

export function useResetGameweek() {
  const invalidate = useInvalidateGameweekDependents()
  const addToast   = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (gameweekId: string) =>
      apiClient.post<ResetGameweekResult>(endpoints.admin.resetGameweek(gameweekId), {}),
    onSuccess: (result) => {
      invalidate()
      addToast({
        type: 'success',
        title: 'Rodada resetada',
        body:  `${result.squadScoresDeleted} scores apagados — rodada voltou para LOCKED`,
      })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao resetar', body: err.message })
    },
  })
}

export function useReopenTransferWindow() {
  const invalidate = useInvalidateGameweekDependents()
  const addToast   = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (gameweekId: string) =>
      apiClient.post<ReopenWindowResult>(endpoints.admin.reopenTransferWindow(gameweekId), {}),
    onSuccess: () => {
      invalidate()
      addToast({
        type: 'success',
        title: 'Janela reaberta',
        body:  'Escalações liberadas para edição.',
      })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao reabrir janela', body: err.message })
    },
  })
}

// ── Gameweek action scheduler ─────────────────────────────────────────────────
// Admin-defined schedules that fire a gameweek action (lock/fetch/process/finalize) on a
// cadence via Hangfire. Manual stand-in while the automatic clock triggers stay deferred.

export type ScheduleAction       = 'LOCK' | 'FETCH' | 'PROCESS' | 'FINALIZE' | 'REOPEN' | 'RESULTS'
export type ScheduleMode         = 'ONCE' | 'INTERVAL' | 'WINDOW' | 'CRON'
export type ScheduleIntervalUnit = 'MINUTES' | 'HOURS'
export type ScheduleStatus       = 'SCHEDULED' | 'DONE' | 'CANCELLED' | 'FAILED'

export type ScheduledJob = {
  id:              string
  gameweekId:      string
  action:          ScheduleAction
  mode:            ScheduleMode
  runAt:           string | null
  cron:            string | null
  intervalMinutes: number | null
  windowStart:     string | null
  windowEnd:       string | null
  status:          ScheduleStatus
  runCount:        number
  lastRunAt:       string | null
  lastError:       string | null
  createdAt:       string
}

// Optional keys are omitted (never set to undefined) by the caller — see ScheduleForm —
// to satisfy exactOptionalPropertyTypes.
export type CreateScheduleInput = {
  action:        ScheduleAction
  mode:          ScheduleMode
  runAt?:        string
  intervalValue?: number
  intervalUnit?: ScheduleIntervalUnit
  windowStart?:  string
  windowEnd?:    string
  cron?:         string
}

export function useGameweekSchedules(gameweekId: string | null) {
  return useQuery({
    queryKey:  adminKeys.schedules(gameweekId ?? ''),
    queryFn:   () => apiClient.get<ScheduledJob[]>(endpoints.admin.schedules.list(gameweekId as string)),
    enabled:   !!gameweekId,
    staleTime: 15_000,   // short so RunCount / status refresh as jobs fire
  })
}

export function useCreateSchedule(gameweekId: string) {
  const qc       = useQueryClient()
  const addToast = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (input: CreateScheduleInput) =>
      apiClient.post<ScheduledJob>(endpoints.admin.schedules.create(gameweekId), input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.schedules(gameweekId) })
      addToast({ type: 'success', title: 'Agendamento criado' })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao agendar', body: err.message })
    },
  })
}

export function useCancelSchedule(gameweekId: string) {
  const qc       = useQueryClient()
  const addToast = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (scheduleId: string) =>
      apiClient.delete<void>(endpoints.admin.schedules.cancel(scheduleId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.schedules(gameweekId) })
      addToast({ type: 'success', title: 'Agendamento cancelado' })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao cancelar', body: err.message })
    },
  })
}

// ── Persons admin (list + DisplayName + Position) ─────────────────────────────

export type AdminPersonItem = {
  personId:    string
  playerId:    string
  externalId:  string
  name:        string
  displayName: string
  teamId:      string
  teamCode:    string
  teamName:    string
  position:    string   // '' when needs admin assignment
  price:       number   // virtual millions, 4.0–15.0
  isAvailable: boolean
}

export async function fetchAdminPersons(
  competitionId: string,
  filter: PersonsFilter,
): Promise<AdminPersonItem[]> {
  const opts: { teamId?: string; position?: string } = {}
  if (filter.teamId)   opts.teamId   = filter.teamId
  if (filter.position) opts.position = filter.position
  return apiClient.get<AdminPersonItem[]>(
    endpoints.admin.persons.list(competitionId, opts),
  )
}

export function useAdminPersons(
  competitionId: string | null,
  filter: PersonsFilter,
) {
  // Backend requires at least one filter (422 otherwise) — don't fire until set.
  const hasFilter = !!(filter.teamId || filter.position)
  return useQuery({
    queryKey: adminKeys.persons(competitionId ?? '', filter),
    queryFn:  () => fetchAdminPersons(competitionId as string, filter),
    enabled:  !!competitionId && hasFilter,
    staleTime: 60_000,
  })
}

export type UpdatePersonDisplayNameResult = {
  personId:    string
  displayName: string
}

export function useUpdatePersonDisplayName() {
  const qc       = useQueryClient()
  const addToast = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: ({ personId, displayName }: { personId: string; displayName: string }) =>
      apiClient.patch<UpdatePersonDisplayNameResult>(
        endpoints.admin.persons.updateDisplayName(personId),
        { displayName },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.personsAll() })
      addToast({ type: 'success', title: 'Nome atualizado' })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao atualizar nome', body: err.message })
    },
  })
}

export type UpdatePlayerPositionResult = {
  playerId:    string
  position:    string
  isAvailable: boolean
}

export function useUpdatePlayerPosition() {
  const qc       = useQueryClient()
  const addToast = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: ({ playerId, position }: { playerId: string; position: string }) =>
      apiClient.patch<UpdatePlayerPositionResult>(
        endpoints.admin.players.updatePosition(playerId),
        { position },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.personsAll() })
      // squad picker reads Player.IsAvailable — setting a valid position flips it true.
      void qc.invalidateQueries({ queryKey: playerKeys.all })
      addToast({ type: 'success', title: 'Posição atualizada' })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao atualizar posição', body: err.message })
    },
  })
}

export type UpdatePlayerPriceResult = {
  playerId: string
  price:    number
}

export function useUpdatePlayerPrice() {
  const qc       = useQueryClient()
  const addToast = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: ({ playerId, price }: { playerId: string; price: number }) =>
      apiClient.patch<UpdatePlayerPriceResult>(
        endpoints.admin.players.updatePrice(playerId),
        { price },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: adminKeys.personsAll() })
      // squad picker shows price — keep it in sync.
      void qc.invalidateQueries({ queryKey: playerKeys.all })
      addToast({ type: 'success', title: 'Preço atualizado' })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao atualizar preço', body: err.message })
    },
  })
}

// ── Seed rosters from API ─────────────────────────────────────────────────────

export type SeedRostersResult = {
  competitionId:             string
  teamsProcessed:            number
  teamsSkippedNoExternalId:  number
  personsCreated:            number
  personsRefreshed:          number
  personsAlreadyExisted:     number
  playersCreated:            number
  playersAlreadyExisted:     number
  playersWithoutPosition:    number
  warnings:                  string[]
}

export function useSeedRosters() {
  const qc       = useQueryClient()
  const addToast = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: ({
      competitionId,
      season,
      leagueId,
      forceRefresh,
    }: {
      competitionId: string
      season:        number
      leagueId?:     string
      forceRefresh:  boolean
    }) =>
      apiClient.post<SeedRostersResult>(
        endpoints.admin.seedRosters(competitionId),
        { season, leagueId: leagueId || null, forceRefresh },
      ),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: adminKeys.personsAll() })
      void qc.invalidateQueries({ queryKey: playerKeys.all })
      addToast({
        type:  'success',
        title: 'Elencos carregados',
        body:  `${result.teamsProcessed} times · ${result.playersCreated} jogadores criados`,
      })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao buscar elencos', body: err.message })
    },
  })
}

// ── Import teams from API ──────────────────────────────────────────────────────

export type SyncTeamsResult = {
  competitionId:  string
  teamsFromApi:   number
  teamsCreated:   number
  teamsUpdated:   number
  groupsAssigned: number
  warnings:       string[]
}

export function useSyncTeams() {
  const qc       = useQueryClient()
  const addToast = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: ({ competitionId, season, leagueId }: {
      competitionId: string
      season:        number
      leagueId:      string
    }) =>
      apiClient.post<SyncTeamsResult>(endpoints.admin.syncTeams(competitionId), { season, leagueId }),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: adminKeys.all })
      void qc.invalidateQueries({ queryKey: leagueKeys.all })
      addToast({
        type:  'success',
        title: 'Times importados',
        body:  `${result.teamsCreated} criados · ${result.teamsUpdated} atualizados · ${result.groupsAssigned} grupos`,
      })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao importar times', body: err.message })
    },
  })
}

// ── Sync injuries / suspensions from API ───────────────────────────────────────

export type SyncInjuriesResult = {
  competitionId:     string
  injuryRowsFromApi: number
  suspended:         number
  injuredOut:        number
  injuredDoubtful:   number
  cleared:           number
  playersNotFound:   number
  warnings:          string[]
}

export function useSyncInjuries() {
  const qc       = useQueryClient()
  const addToast = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: ({ competitionId, season, leagueId }: {
      competitionId: string
      season:        number
      leagueId:      string
    }) =>
      apiClient.post<SyncInjuriesResult>(endpoints.admin.syncInjuries(competitionId), { season, leagueId }),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: adminKeys.all })
      void qc.invalidateQueries({ queryKey: playerKeys.all })
      addToast({
        type:  'success',
        title: 'Lesões sincronizadas',
        body:  `${result.suspended} susp. · ${result.injuredOut + result.injuredDoubtful} lesionados · ${result.cleared} liberados`,
      })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao sincronizar lesões', body: err.message })
    },
  })
}

// ── Sync eliminations from the published bracket ───────────────────────────────
// Marks teams eliminated when a later round's fixtures are resolved and the team is
// absent from them — covers the group stage (absent from R32) and every knockout
// round. Reads only local fixtures; run after importing each new round. Idempotent.

export type EliminatedTeamInfo = {
  code:         string
  name:         string
  afterGameweek: number
}

export type SyncEliminationsResult = {
  competitionId:   string
  teamsEliminated: number
  creditsGranted:  number
  eliminated:      EliminatedTeamInfo[]
  warnings:        string[]
}

export function useSyncEliminations() {
  const qc       = useQueryClient()
  const addToast = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (competitionId: string) =>
      apiClient.post<SyncEliminationsResult>(endpoints.admin.syncEliminations(competitionId), {}),
    onSuccess: (result) => {
      // ELIMINADO badge + add-button state read Team.IsEliminated via the player list;
      // squads/standings surface eliminated players too.
      void qc.invalidateQueries({ queryKey: adminKeys.all })
      void qc.invalidateQueries({ queryKey: playerKeys.all })
      void qc.invalidateQueries({ queryKey: leagueKeys.all })
      addToast({
        type:  'success',
        title: 'Eliminações sincronizadas',
        body:  result.teamsEliminated > 0
          ? `${result.teamsEliminated} seleção(ões) eliminada(s): ${result.eliminated.map(e => e.code).join(', ')} · ${result.creditsGranted} transferência(s) creditada(s)`
          : 'Nenhuma nova eliminação.',
      })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao sincronizar eliminações', body: err.message })
    },
  })
}

// ── Import schedule (gameweeks + matches) from API ─────────────────────────────

export type SyncScheduleResult = {
  competitionId:    string
  fixturesFromApi:  number
  gameweeksCreated: number
  matchesCreated:   number
  matchesUpdated:   number
  warnings:         string[]
}

export function useSyncSchedule() {
  const qc       = useQueryClient()
  const addToast = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: ({ competitionId, season, leagueId }: {
      competitionId: string
      season:        number
      leagueId:      string
    }) =>
      apiClient.post<SyncScheduleResult>(endpoints.admin.syncSchedule(competitionId), { season, leagueId }),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: adminKeys.all })
      void qc.invalidateQueries({ queryKey: leagueKeys.all })
      addToast({
        type:  'success',
        title: 'Calendário importado',
        body:  `${result.gameweeksCreated} rodadas · ${result.matchesCreated} partidas criadas`,
      })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao importar calendário', body: err.message })
    },
  })
}

// ── Generate H2H fixtures for newly imported gameweeks ─────────────────────────
// H2H schedules are fixed at the first GW lock, but knockout gameweeks only appear
// in the data provider mid-competition. After importing a new phase's calendar, this
// appends fixtures for the new gameweeks to every locked H2H league in the competition.
// Idempotent — re-running once everything is scheduled creates nothing.

export type ExtendH2HFixturesResult = {
  leaguesProcessed: number
  fixturesCreated:  number
}

export function useExtendH2HFixtures() {
  const qc       = useQueryClient()
  const addToast = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (competitionId: string) =>
      apiClient.post<ExtendH2HFixturesResult>(
        endpoints.admin.extendH2HFixtures(competitionId), {}),
    onSuccess: (result) => {
      // H2H fixtures back the Confrontos tab + standings.
      void qc.invalidateQueries({ queryKey: leagueKeys.all })
      addToast({
        type:  'success',
        title: 'Confrontos H2H gerados',
        body:  result.fixturesCreated > 0
          ? `${result.fixturesCreated} confrontos em ${result.leaguesProcessed} liga(s)`
          : 'Nenhum confronto novo — tudo já estava agendado.',
      })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao gerar confrontos', body: err.message })
    },
  })
}

// ── Price preview (dry-run) ───────────────────────────────────────────────────
// Computes suggested prices for a team's players from last-season stats. Writes
// nothing — the tuning tool for the pricing constants before any full run.

export type PreviewPlayerPriceItem = {
  playerId:        string
  externalId:      string
  displayName:     string
  position:        string
  currentPrice:    number
  suggestedPrice:  number
  clubMinutes:     number
  ntMinutes:       number
  clubRating:      number
  goals:           number
  assists:         number
  ntGoals:         number
  ntAssists:       number
  defActions:      number
  primaryLeagueId: string | null
  leagueStrength:  number
  nationStrength:  number
  playFactor:      number
  ev:              number
  lowData:         boolean
  fetchError:      boolean
}

export type PreviewPricesResult = {
  teamId:           string
  teamCode:         string
  teamName:         string
  seasons:          number[]
  playersEvaluated: number
  lowDataCount:     number
  fetchErrorCount:  number
  warnings:         string[]
  players:          PreviewPlayerPriceItem[]
}

export type PricingTuning = {
  priceScale:    number
  fwdMultiplier: number
  midMultiplier: number
  defMultiplier: number
  gkMultiplier:  number
}

export const DEFAULT_TUNING: PricingTuning = {
  priceScale:    0.047,
  fwdMultiplier: 1.0,
  midMultiplier: 1.0,
  defMultiplier: 1.0,
  gkMultiplier:  1.0,
}

export function usePreviewPrices() {
  const addToast = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: ({
      competitionId,
      teamId,
      seasons,
      tuning,
    }: {
      competitionId: string
      teamId:        string
      seasons:       number[]
      tuning:        PricingTuning
    }) =>
      apiClient.post<PreviewPricesResult>(
        endpoints.admin.previewPrices(competitionId),
        { teamId, seasons, ...tuning },
      ),
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao gerar prévia', body: err.message })
    },
  })
}

// ── Save reviewed prices (per nation) ─────────────────────────────────────────

export type ApplyPricesResult = {
  applied:  number
  skipped:  number
  warnings: string[]
}

export function useApplyPrices() {
  const qc       = useQueryClient()
  const addToast = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: ({
      competitionId,
      prices,
    }: {
      competitionId: string
      prices:        { playerId: string; price: number }[]
    }) =>
      apiClient.post<ApplyPricesResult>(
        endpoints.admin.applyPrices(competitionId),
        { prices },
      ),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: adminKeys.personsAll() })
      void qc.invalidateQueries({ queryKey: playerKeys.all })
      addToast({
        type:  'success',
        title: 'Preços salvos',
        body:  `${result.applied} aplicados${result.skipped > 0 ? ` · ${result.skipped} ignorados` : ''}`,
      })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao salvar preços', body: err.message })
    },
  })
}
